import React, { useEffect, useRef } from "react";
import type RealtimeKitClient from "@cloudflare/realtimekit";
// `useLanguage` é apenas uma fábrica de tradutor (não um hook React); importamos
// com um alias neutro para poder criá-lo uma única vez no nível do módulo sem
// violar a regra de hooks do ESLint.
import { RtkMeeting, provideRtkDesignSystem, useLanguage as createRtkI18n } from "@cloudflare/realtimekit-react-ui";
import { rtkLanguagePtBr } from "./rtkLanguagePtBr";

// Inicializa o design system uma vez.
if (typeof document !== "undefined") {
  provideRtkDesignSystem(document.documentElement, { theme: "dark" });
}

// Tradutor pt-BR para toda a interface da sala (botões, avisos, notificações).
// Mescla o pt-BR sobre o idioma padrão do SDK e é repassado ao RtkMeeting via `t`.
const tPtBr = createRtkI18n(rtkLanguagePtBr);

interface DyteMeetProps {
  meeting: RealtimeKitClient;
  /**
   * Encaminha o evento `roomLeft` do Dyte com o `state` original
   * ('left' | 'ended' | 'kicked' | 'rejected' | 'disconnected' | 'failed' ...).
   * Quem decide o que fazer (encerrar x reconectar) é o Room, que distingue
   * saída legítima de queda de rede para não derrubar a sala de todos.
   */
  onMeetingEnd?: (state?: string) => void;
}

const DyteMeet: React.FC<DyteMeetProps> = ({ meeting, onMeetingEnd }) => {
  const meetingEl = useRef<HTMLRtkMeetingElement>(null);

  useEffect(() => {
    const handleRoomLeft = ({ state }: { state?: string }) => {
      onMeetingEnd?.(state);
    };

    meeting.self.on("roomLeft", handleRoomLeft);

    return () => {
      meeting.self.removeListener("roomLeft", handleRoomLeft);

      // Remover listeners de stageStatusUpdate que causam memory leak.
      if (meeting.stage && typeof meeting.stage.removeAllListeners === "function") {
        meeting.stage.removeAllListeners("stageStatusUpdate");
      }
    };
  }, [meeting, onMeetingEnd]);

  return (
    <div className="h-full w-full relative">
      <RtkMeeting
        meeting={meeting}
        ref={meetingEl}
        t={tPtBr}
        showSetupScreen={false}
        className="w-full h-full"
        leaveOnUnmount={false}
        mode="fill"
      />
    </div>
  );
};

export default DyteMeet;
